import React, { useState, useCallback, useEffect, useRef } from 'react';
import Draggable, { DraggableData, DraggableEvent } from 'react-draggable';
import Window from './Window';
import Taskbar from './Taskbar';
import { WindowData, DesktopIcon, MenuItem } from '../types';

// Program imports
import Notepad from './programs/Notepad';
import ImageGallery from './programs/ImageGallery';
import Winamp from './programs/Winamp';
import VideoPlayer from './programs/VideoPlayer';
import FolderWindow from './programs/FolderWindow';
import DontOpen from './programs/DontOpen';
import Colorize from './programs/Colorize';

const Desktop: React.FC = () => {
  const [windows, setWindows] = useState<WindowData[]>([]);
  const [icons, setIcons] = useState<DesktopIcon[]>([]);
  const [selectedIcon, setSelectedIcon] = useState<string | null>(null);
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number } | null>(null);
  const [activeWindowId, setActiveWindowId] = useState<string | null>(null);
  const [maxZIndex, setMaxZIndex] = useState(1000);
  const [backgroundImage, setBackgroundImage] = useState<string | null>(null);
  const [contactEmail, setContactEmail] = useState<string>('lead@aiaccessconsulting.com');
  const [iconToOpen, setIconToOpen] = useState<string | null>(null);
  const desktopRef = useRef<HTMLDivElement>(null);
  const lastTapRef = useRef<{ iconId: string; time: number } | null>(null);

  // Fetch desktop icons from API
  useEffect(() => {
    const fetchIcons = async () => {
      try {
        const response = await fetch('/api/desktop/icons');
        const data = await response.json();

        // Load saved positions from localStorage
        const savedPositions = JSON.parse(localStorage.getItem('iconPositions') || '{}');

        // Merge API data with localStorage positions (localStorage takes precedence)
        const iconsWithPositions = data.icons.map((icon: DesktopIcon) => ({
          ...icon,
          x: savedPositions[icon.id]?.x ?? icon.x ?? 0,
          y: savedPositions[icon.id]?.y ?? icon.y ?? 0,
        }));

        setIcons(iconsWithPositions.sort((a: DesktopIcon, b: DesktopIcon) => a.position - b.position));
      } catch (error) {
        console.error('Failed to load desktop icons:', error);
      }
    };
    fetchIcons();
  }, []);

  // Fetch desktop background from API
  useEffect(() => {
    const fetchBackground = async () => {
      try {
        const response = await fetch('/api/desktop/background');
        const data = await response.json();
        setBackgroundImage(data.background);
      } catch (error) {
        console.error('Failed to load desktop background:', error);
      }
    };
    fetchBackground();
  }, []);

  // Fetch contact email from API
  useEffect(() => {
    const fetchContactEmail = async () => {
      try {
        const response = await fetch('/api/content/contact_email');
        const data = await response.json();
        if (data.value) {
          setContactEmail(data.value);
        }
      } catch (error) {
        console.error('Failed to load contact email:', error);
      }
    };
    fetchContactEmail();
  }, []);

  const handleIconDragStop = useCallback((iconId: string, e: DraggableEvent, data: DraggableData) => {
    const icon = icons.find(i => i.id === iconId);
    if (!icon) return;

    // Check if icon actually moved (drag) vs just clicked/tapped
    const didMove = Math.abs(data.x - (icon.x || 0)) > 5 || Math.abs(data.y - (icon.y || 0)) > 5;

    if (didMove) {
      // Update local state
      setIcons((prevIcons) =>
        prevIcons.map((i) =>
          i.id === iconId ? { ...i, x: data.x, y: data.y } : i
        )
      );

      // Save to localStorage for all users
      const savedPositions = JSON.parse(localStorage.getItem('iconPositions') || '{}');
      savedPositions[iconId] = { x: data.x, y: data.y };
      localStorage.setItem('iconPositions', JSON.stringify(savedPositions));
    } else {
      // Icon didn't move - this was a tap/click
      // Detect double-tap for mobile (within 500ms)
      const now = Date.now();
      const lastTap = lastTapRef.current;

      if (lastTap && lastTap.iconId === iconId && now - lastTap.time < 500) {
        // Double-tap detected - trigger window opening
        lastTapRef.current = null;
        setIconToOpen(iconId);
      } else {
        // Single tap - just select the icon
        lastTapRef.current = { iconId, time: now };
        setSelectedIcon(iconId);
      }
    }
  }, [icons]);

  const getNextZIndex = useCallback(() => {
    setMaxZIndex((prev) => prev + 1);
    return maxZIndex + 1;
  }, [maxZIndex]);

  const createWindow = useCallback(
    (icon: DesktopIcon) => {
      const existingWindow = windows.find((w) => w.id === icon.id);
      if (existingWindow) {
        // Focus existing window
        setWindows((prev) =>
          prev.map((w) =>
            w.id === icon.id
              ? { ...w, minimized: false, zIndex: getNextZIndex() }
              : w
          )
        );
        setActiveWindowId(icon.id);
        return;
      }

      const baseWindow: Omit<WindowData, 'component' | 'title' | 'width' | 'height'> = {
        id: icon.id,
        icon: icon.icon,
        x: 100 + windows.length * 30,
        y: 100 + windows.length * 30,
        minimized: false,
        maximized: false,
        zIndex: getNextZIndex(),
        resizable: true,
      };

      // Handle contact/browser icon - open mailto link instead of window
      if (icon.program === 'browser') {
        window.location.href = `mailto:${contactEmail}`;
        return;
      }

      let newWindow: WindowData;

      switch (icon.program) {
        case 'notepad':
          newWindow = {
            ...baseWindow,
            title: icon.label,
            component: 'notepad',
            width: 600,
            height: 400,
            resizable: true,
          };
          break;
        case 'gallery':
          newWindow = {
            ...baseWindow,
            title: 'AI Gallery',
            component: 'gallery',
            width: 800,
            height: 600,
            resizable: true,
          };
          break;
        case 'folder':
          newWindow = {
            ...baseWindow,
            title: 'Videos',
            component: 'folder',
            width: 500,
            height: 400,
            resizable: true,
            props: { folderType: 'videos' },
          };
          break;
        case 'recycle':
          newWindow = {
            ...baseWindow,
            title: 'Recycle Bin',
            component: 'folder',
            width: 500,
            height: 400,
            resizable: true,
            props: { folderType: 'recycle' },
          };
          break;
        case 'colorize':
          newWindow = {
            ...baseWindow,
            title: 'Colorize.exe',
            component: 'colorize',
            width: 700,
            height: 500,
            resizable: true,
          };
          break;
        case 'winamp':
          newWindow = {
            ...baseWindow,
            title: 'Winamp',
            component: 'winamp',
            width: 600,
            height: 400,
            resizable: true,
          };
          break;
        case 'dontopen':
          newWindow = {
            ...baseWindow,
            title: 'SYSTEM_ERROR.exe',
            component: 'dontopen',
            width: window.innerWidth,
            height: window.innerHeight - 32,
            x: 0,
            y: 0,
            resizable: false,
            maximized: true,
          };
          break;
        default:
          return;
      }

      setWindows((prev) => [...prev, newWindow]);
      setActiveWindowId(newWindow.id);
    },
    [windows, getNextZIndex, contactEmail]
  );

  // Handle icon opening from double-tap detection
  useEffect(() => {
    if (iconToOpen) {
      const icon = icons.find(i => i.id === iconToOpen);
      if (icon) {
        createWindow(icon);
      }
      setIconToOpen(null);
    }
  }, [iconToOpen, icons, createWindow]);

  const closeWindow = useCallback((id: string) => {
    setWindows((prev) => prev.filter((w) => w.id !== id));
    setActiveWindowId(null);
  }, []);

  const minimizeWindow = useCallback((id: string) => {
    setWindows((prev) =>
      prev.map((w) => (w.id === id ? { ...w, minimized: true } : w))
    );
    setActiveWindowId(null);
  }, []);

  const maximizeWindow = useCallback((id: string) => {
    setWindows((prev) =>
      prev.map((w) =>
        w.id === id ? { ...w, maximized: !w.maximized } : w
      )
    );
  }, []);

  const focusWindow = useCallback(
    (id: string) => {
      setWindows((prev) =>
        prev.map((w) => (w.id === id ? { ...w, zIndex: getNextZIndex(), minimized: false } : w))
      );
      setActiveWindowId(id);
    },
    [getNextZIndex]
  );

  const handleIconClick = (iconId: string) => {
    setSelectedIcon(iconId);
  };

  const handleIconDoubleClick = (e: React.MouseEvent, icon: DesktopIcon) => {
    e.preventDefault();
    e.stopPropagation();

    // Prevent duplicate opens from rapid double-clicks
    if (iconToOpen === icon.id) return;

    setIconToOpen(icon.id);
    createWindow(icon);

    // Reset after a short delay
    setTimeout(() => setIconToOpen(null), 500);
  };

  const handleDesktopClick = (e: React.MouseEvent) => {
    if (e.target === desktopRef.current) {
      setSelectedIcon(null);
      setContextMenu(null);
    }
  };

  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    setContextMenu({ x: e.clientX, y: e.clientY });
  };

  const handleRefresh = () => {
    // Flicker effect
    if (desktopRef.current) {
      desktopRef.current.style.opacity = '0';
      setTimeout(() => {
        if (desktopRef.current) {
          desktopRef.current.style.opacity = '1';
        }
      }, 100);
    }
    setContextMenu(null);
  };

  const handleAutoArrange = async () => {
    try {
      // Clear localStorage positions
      localStorage.removeItem('iconPositions');

      // Call backend to reset positions
      const response = await fetch('/api/desktop/icons/reset', {
        method: 'POST',
      });

      if (response.ok) {
        // Reload icons from database
        const iconsResponse = await fetch('/api/desktop/icons');
        const data = await iconsResponse.json();
        setIcons(data.icons.sort((a: DesktopIcon, b: DesktopIcon) => a.position - b.position));
      }
    } catch (error) {
      console.error('Auto arrange failed:', error);
    }
    setContextMenu(null);
  };

  const handleProperties = () => {
    const propertiesWindow: WindowData = {
      id: 'properties',
      title: 'System Properties',
      component: 'properties',
      icon: '⚙️',
      x: 200,
      y: 150,
      width: 400,
      height: 300,
      minimized: false,
      maximized: false,
      zIndex: getNextZIndex(),
      resizable: false,
    };
    setWindows((prev) => [...prev, propertiesWindow]);
    setContextMenu(null);
  };

  useEffect(() => {
    const handleClickOutside = () => {
      setContextMenu(null);
    };

    if (contextMenu) {
      document.addEventListener('click', handleClickOutside);
    }

    return () => document.removeEventListener('click', handleClickOutside);
  }, [contextMenu]);

  const renderWindowContent = (window: WindowData) => {
    switch (window.component) {
      case 'notepad':
        return <Notepad />;
      case 'gallery':
        return <ImageGallery />;
      case 'winamp':
        return <Winamp />;
      case 'folder':
        return (
          <FolderWindow
            folderType={window.props?.folderType || 'videos'}
            onFileOpen={(file: any) => {
              if (window.props?.folderType === 'videos') {
                const videoWindow: WindowData = {
                  id: `video-${file.name}`,
                  title: file.name,
                  component: 'video',
                  icon: '🎬',
                  x: 150,
                  y: 100,
                  width: 800,
                  height: 600,
                  minimized: false,
                  maximized: false,
                  zIndex: getNextZIndex(),
                  resizable: true,
                  props: { file },
                };
                setWindows((prev) => [...prev, videoWindow]);
              }
            }}
          />
        );
      case 'video':
        return <VideoPlayer file={window.props?.file} />;
      case 'dontopen':
        return <DontOpen onClose={() => closeWindow(window.id)} />;
      case 'colorize':
        return <Colorize />;
      case 'properties':
        return (
          <div style={{ padding: '16px', fontFamily: 'MS Sans Serif' }}>
            <h3>System Properties</h3>
            <div style={{ marginTop: '16px' }}>
              <p><strong>Processor:</strong> AI Neural Core 9000 GHz</p>
              <p><strong>RAM:</strong> Unlimited Creativity</p>
              <p><strong>Operating System:</strong> Windows 95 (AI Edition)</p>
              <p><strong>Graphics:</strong> Diffusion Accelerator 3000</p>
              <p><strong>Network:</strong> Neural Network Adapter</p>
            </div>
            <div style={{ marginTop: '24px', textAlign: 'center' }}>
              <button onClick={() => closeWindow(window.id)}>OK</button>
            </div>
          </div>
        );
      default:
        return <div>Unknown program</div>;
    }
  };

  return (
    <div
      ref={desktopRef}
      className="desktop"
      onClick={handleDesktopClick}
      onContextMenu={handleContextMenu}
      style={backgroundImage ? {
        backgroundImage: `url(${backgroundImage})`
      } : undefined}
    >
      <div className="desktop-icons">
        {icons.map((icon) => (
          <Draggable
            key={icon.id}
            position={{ x: icon.x || 0, y: icon.y || 0 }}
            onStop={(e: DraggableEvent, data: DraggableData) => handleIconDragStop(icon.id, e, data)}
            bounds="parent"
            enableUserSelectHack={false}
          >
            <div
              className={`desktop-icon ${selectedIcon === icon.id ? 'selected' : ''}`}
              onClick={() => handleIconClick(icon.id)}
              onDoubleClick={(e) => handleIconDoubleClick(e, icon)}
              style={{ position: 'absolute', zIndex: 2 }}
            >
              <div className="desktop-icon-image">{icon.icon}</div>
              <div className="desktop-icon-label">{icon.label}</div>
            </div>
          </Draggable>
        ))}
      </div>

      {windows.map((window) => (
        <div key={window.id} style={{ position: 'absolute', top: 0, left: 0, pointerEvents: 'none', width: '100%', height: '100%' }}>
          <Window
            window={window}
            onClose={closeWindow}
            onMinimize={minimizeWindow}
            onMaximize={maximizeWindow}
            onFocus={focusWindow}
          >
            {renderWindowContent(window)}
          </Window>
        </div>
      ))}

      {contextMenu && (
        <div
          className="context-menu"
          style={{ left: contextMenu.x, top: contextMenu.y }}
        >
          <div className="context-menu-item" onClick={handleRefresh}>
            Refresh
          </div>
          <div className="context-menu-divider" />
          <div className="context-menu-item" onClick={handleAutoArrange}>
            Auto Arrange Icons
          </div>
          <div className="context-menu-divider" />
          <div className="context-menu-item disabled">New</div>
          <div className="context-menu-divider" />
          <div className="context-menu-item" onClick={handleProperties}>
            Properties
          </div>
        </div>
      )}

      <Taskbar
        windows={windows}
        icons={icons}
        onWindowSelect={focusWindow}
        onStartMenuClick={createWindow}
        activeWindowId={activeWindowId}
      />
    </div>
  );
};

export default Desktop;
